<template>
  <div class="cyber-card p-6">
    <div class="flex items-center space-x-3 pb-3">
      <div class="flex items-center space-x-4 flex-1">
        <div class="w-12 h-12 rounded-lg flex items-center justify-center" :class="[`bg-${variant}-600`]">
          <slot name="icon" />
        </div>

        <div class="flex-1">
          <h4 class="font-semibold">{{ LicenseType[id] }}
            <span v-if="modelValue" class="inline-flex items-center justify-center rounded-md border font-medium w-fit whitespace-nowrap shrink-0 mr-1 gap-1 border-transparent text-white bg-green-600 text-xs px-2 py-0.5">
              <font-awesome-icon :icon="['fas','circle-check']" class="text-white-800" />
            </span>
          </h4>
          <p class="text-xs text-gray-600">{{ subtitle }}</p>
        </div>
      </div>
      <Toggle :id="id"
        :model-value="modelValue"
        class="ml-4"
        @update:modelValue="$emit('update:modelValue', $event)" />
    </div>

    <div v-if="modelValue" class="[&:last-child]:pb-6 pt-0 space-y-3">
      <div class="grid grid-cols-3 gap-2">
        <div class="space-y-1">
          <FormGroup :label="$t('License.Quantity')" html-for="quantity" :noMargin="true">
            <CGInput
              id="quantity"
              v-model="localQuantity"
              name="quantity"
              :min="1"
              :max="LicenseMaxQuantity"
              type="number"
              required
              :errored="localQuantity < 1 || localQuantity > LicenseMaxQuantity"
            />
          </FormGroup>
        </div>
        <div class="space-y-1">
          <FormGroup :label="$t('License.StartDate')" html-for="start_date" :noMargin="true">
            <DatePicker
              id="start_date"
              v-model="localStartDate"
              :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
              :locale="$i18n.locale"
              no-margin
              :errored="!validStartDate"
            ></DatePicker>
          </FormGroup>
        </div>
        <div class="space-y-1">
          <FormGroup :label="$t('License.Duration')" html-for="license_length" :noMargin="true">
            <CGSimpleSelect
              id="license_length"
              v-model="localLicenseLenght"
              :options="LicenseMonthsAvailabilityCreation.filter(i => i.roles.includes($methods.getBaseRole($profile.currentRole)))"
              auto-width
            />
          </FormGroup>
        </div>
      </div>

      <div class="flex items-center justify-between text-xs bg-white rounded border p-2">
        <div class="flex items-center space-x-2">
          <Icon name="calendar" :class="[`text-${variant}-500`]" />
          <span class="text-gray-600">{{ $t('License.ExpiryDate') }}</span>
          <span class="font-medium" :class="[`text-${variant}-600`]">{{ dueDate }}</span>
        </div>
        <span class="inline-flex items-center justify-center rounded-md border px-2 py-0.5 font-medium w-fit whitespace-nowrap shrink-0 gap-1 text-xs">{{ totalLicenses }}</span>
      </div>
    </div>

    <div v-else class="text-center py-4 text-gray-600">
      <div class="text-xs opacity-75">{{ $t('Company.InactiveService') }}</div>
      <div class="text-xs opacity-50">{{ $t('Company.EnableToggle') }}</div>
    </div>
  </div>
</template>

<script>
import { LicenseMonthsAvailabilityCreation, LicenseType, LicenseMaxQuantity } from '@/common/constants';
import { DateTime } from 'luxon';

export default {
  name: 'LicenseCard',
  props: {
    id: {
      type: [String, Number],
      required: true
    },
    subtitle: String,
    modelValue: {
      type: Boolean,
      required: true
    },
    variant: {
      type: String,
      default: 'gray', // e.g. 'gray', 'blue', 'yellow', 'red', etc.
    },
    quantity: {
      type: String,
      default: "1"
    },
    start_date: {
      type: [DateTime, String],
      default: () => this.$dateTime.now() // Default to today
    },
    license_length: {
      type: Number,
      default: 12 // Default duration
    }
  },
  data: function() {
    return {
      LicenseType,
      LicenseMaxQuantity,
      LicenseMonthsAvailabilityCreation
    };
  },
  mounted() {
    if (!this.start_date) {
      const today = this.$dateTime.now().toFormat('yyyy-MM-dd');
      this.$emit('update:start_date', today);
    }
    if(!this.license_length) {
      this.$emit('update:license_length', '12'); // Default to 12 months
    }
  },
  computed: {
    localQuantity: {
      get() {
        return this.quantity;
      },
      set(val) {
        this.$emit('update:quantity', val);
      }
    },
    localLicenseLenght: {
      get() {
        return this.license_length;
      },
      set(val) {
        this.$emit('update:license_length', val);
      }
    },
    localStartDate: {
      get() {
        return this.$dateTime.fromFormat(this.start_date, 'yyyy-MM-dd').toFormat('yyyy-MM-dd');
      },
      set(val) {
        this.$emit('update:start_date', val);
      }
    },
    dueDate() {
      if(!this.localStartDate || !this.localLicenseLenght) {
        return '';
      }
      return this.$dateTime.fromISO(this.localStartDate).plus({ months: this.localLicenseLenght}).toFormat('dd/MM/yyyy');
    },
    totalLicenses() {
      return this.$t('Company.LicensesNumber', parseInt(this.localQuantity, 10));
    },
    validStartDate() {
      let start_date = this.$dateTime.fromFormat(this.localStartDate, 'yyyy-MM-dd');
      return this.$dateTime.now().minus({ days: 30 }).startOf("day") <= start_date.startOf("day");
    }
  }
};
</script>
